import { toJS } from 'mobx';

import Collection from '@/collection/entity/Collection';
import { CollectionType } from '@/collection/entity/CollectionType';
import {
  CollectionItem,
  CollectionSubscriptionItem,
  createDriveItem,
  DriveItem,
  DriveItemType,
  FolderItem,
  UrlItem,
} from '@/drive/entities/DriveItem/DriveItem';
import { DriveItemId } from '@/drive/entities/DriveItemId';
import { TransientDriveItem } from '@/drive/entities/transientDriveItem';
import { cleanUpForm, ForwardForm } from '@/forwarding/types/ForwardForm';
import { BaseProductType } from '@/product/constants/productTypes';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { ProjectDataAccess } from '@/security/types/DataAccess';

export class TemporaryDriveItemFactory {
  private lastTemporaryId = -1;

  private nextId(): DriveItemId {
    this.lastTemporaryId -= 1;

    return this.lastTemporaryId;
  }

  createFolder(form: ForwardForm): FolderItem {
    return new FolderItem({
      id: this.nextId(),
      type: DriveItemType.Folder,
      title: form.title,
      description: form.description,
      preview: form.previewUrl ? { url: form.previewUrl } : null,
      count: 0,
      access: GlobalAccessLevel.Restricted,
      dataAccess: ProjectDataAccess.HideProjectSource,
      canEdit: true,
      canManageAccess: true,
    });
  }

  createUrl(form: ForwardForm): UrlItem {
    return new UrlItem({
      id: this.nextId(),
      type: DriveItemType.Url,
      title: form.title,
      description: form.description,
      url: form.url,
      preview: form.previewUrl ? { url: form.previewUrl } : null,
      canEdit: true,
      canManageAccess: true,
    });
  }

  createCollection(form: ForwardForm, productType: BaseProductType): CollectionItem {
    const collectionType = Collection.getTypeByProductType(productType);

    return new CollectionItem({
      id: this.nextId(),
      type: DriveItemType.Collection,
      title: form.title,
      description: form.description,
      collectionType,
      collectionId: '',
      count: 0,
      access: GlobalAccessLevel.Restricted,
      dataAccess: ProjectDataAccess.HideProjectSource,
      canEdit: true,
      canManageAccess: true,
    });
  }

  createMaterializedCollection(subscription: CollectionSubscriptionItem): CollectionItem {
    return new CollectionItem({
      id: this.nextId(),
      type: DriveItemType.Collection,
      title: subscription.title,
      description: subscription.description,
      collectionType: subscription.collectionType,
      collectionId: '',
      count: subscription.count,
      access: GlobalAccessLevel.Restricted,
      dataAccess: ProjectDataAccess.HideProjectSource,
      canEdit: true,
      canManageAccess: true,
    });
  }

  createCopy(source: TransientDriveItem, form: ForwardForm, readOnly: boolean): DriveItem {
    const { title, description } = cleanUpForm(form, { readOnly });
    const item = createDriveItem({ ...toJS(source), id: this.nextId() });

    item.setTitle(title || item.title);
    item.setDescription(description ?? item.description ?? '');

    return item;
  }

  createCollectionSubscription(
    form: ForwardForm,
    collectionId: string,
    collectionType: CollectionType,
    canEdit: boolean,
    canManageAccess: boolean,
  ): CollectionSubscriptionItem {
    return new CollectionSubscriptionItem({
      id: this.nextId(),
      type: DriveItemType.CollectionSubscription,
      title: form.title,
      description: form.description,
      collectionId,
      collectionType,
      count: 0,
      canEdit,
      canManageAccess,
    });
  }
}
